import { useEffect, useState } from "react";
import api from "@/utils/api";
import { Receipt, Calendar, CreditCard, ArrowDownLeft, FileText } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { getToken, getUser } from "@/utils/auth";
import { useSettings } from "@/context/SettingsContext";

const MemberLedger = () => {
  const { formatCurrency, formatDate } = useSettings();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [receipts, setReceipts] = useState([]);

  useEffect(() => {
    fetchMemberDataAndLedger();
  }, []);

  const fetchMemberDataAndLedger = async () => {
    try {
      // First get member profile to get the correct name for filtering
      let memberName = user?.full_name || '';
      
      try {
        const response = await api.get('/members/me');
        const member = response.data.member || (response.data.id ? response.data : null);
        if (member) {
          memberName = member.name;
        } else {
          // Fallback search
          const membersResp = await api.get('/members');
          const candidate = membersResp.data.find((m) => 
            (user?.email && (m.email || '').toLowerCase() === (user.email || '').toLowerCase())
          );
          if (candidate) {
            memberName = candidate.name;
          }
        }
      } catch (e) {
        console.warn("Could not fetch member profile, using user name", e);
      }

      // Now fetch ledger
      const ledgerResp = await api.get('/ledger');
      const entries = Array.isArray(ledgerResp.data) ? ledgerResp.data : [];
      
      // Filter for this member's receipts
      const memberReceipts = entries.filter((e) =>
        e.type === 'receipt' && (e.member_name || '').toLowerCase() === (memberName || '').toLowerCase()
      ).sort((a, b) => new Date(b.date) - new Date(a.date));

      setReceipts(memberReceipts);
      setLoading(false);
    } catch (error) {
      console.error("Failed to load ledger:", error);
      toast.error("Failed to load payment history");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lime-500"></div>
      </div>
    );
  }

  const totalPaid = receipts.reduce((sum, r) => sum + (r.amount || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            My Payments
          </h1>
          <p className="text-zinc-500 mt-1">History of your payments and receipts</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg px-4 py-2 shadow-sm w-full md:w-auto">
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Total Paid</p>
          <p className="text-xl font-bold text-green-600">{formatCurrency(totalPaid)}</p>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {receipts.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-xl p-8 text-center text-zinc-500">
            No payment history found
          </div>
        ) : (
          receipts.map((receipt) => (
            <div key={receipt.id} className="bg-white border border-zinc-200 rounded-xl p-4 shadow-sm">
              <div className="flex justify-between items-start mb-3">
                <Badge variant="secondary" className="bg-blue-50 text-blue-700 hover:bg-blue-100">
                  {receipt.category}
                </Badge>
                <div className="font-bold text-zinc-900">
                  {formatCurrency(receipt.amount)}
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-zinc-600">
                  <Calendar size={14} />
                  <span>{formatDate(receipt.date)}</span>
                </div>
                <div className="flex items-center gap-2 text-zinc-600 capitalize">
                  <CreditCard size={14} />
                  <span>{(receipt.payment_method || 'cash').replace('_', ' ')}</span>
                </div>
                
                {receipt.description && (
                  <div className="text-zinc-600 text-sm mt-2 bg-zinc-50 p-2 rounded border border-zinc-100">
                    {receipt.description}
                  </div>
                )}
                
                {receipt.reference_number && (
                  <div className="text-zinc-400 text-xs mt-1 pt-2 border-t border-zinc-100">
                    Ref: {receipt.reference_number}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white border border-zinc-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Method</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">Ref</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {receipts.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-zinc-500">
                    No payment history found
                  </td>
                </tr>
              ) : (
                receipts.map((receipt) => (
                  <tr key={receipt.id} className="hover:bg-zinc-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-zinc-100 rounded-lg text-zinc-500">
                          <Calendar size={16} />
                        </div>
                        <span className="font-medium text-zinc-900">{formatDate(receipt.date)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="secondary" className="bg-blue-50 text-blue-700 hover:bg-blue-100">
                        {receipt.category}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-zinc-900">{receipt.description || '-'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-zinc-600 capitalize">
                        <CreditCard size={14} />
                        {(receipt.payment_method || 'cash').replace('_', ' ')}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-bold text-zinc-900">
                        {formatCurrency(receipt.amount)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-zinc-500">
                      {receipt.reference_number || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MemberLedger;
